import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue, User, Voter, VoterGroup } from '../../types';
import React, { useEffect, useState } from 'react';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, orderBy, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import { QRCodeSVG } from 'qrcode.react';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';

interface VoterDetailsProps {
  voter?: Voter;
  showBackButton?: boolean;
}

const VoterDetails: React.FC<VoterDetailsProps> = ({ 
  voter: propVoter,
  showBackButton = true
}) => {
  const { voterId } = useParams<{ voterId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [voter, setVoter] = useState<Voter | null>(propVoter || null);
  const [pollResponses, setPollResponses] = useState<PollResponse[]>([]);
  const [voterGroups, setVoterGroups] = useState<VoterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [upmergeClicked, setUpmergeClicked] = useState(false);

  // QR Code State
  const [isQRCodeDialogOpen, setIsQRCodeDialogOpen] = useState(false);

  // Release State
  const [isStubReleaseDialogOpen, setIsStubReleaseDialogOpen] = useState(false);
  const [isFinalReleaseDialogOpen, setIsFinalReleaseDialogOpen] = useState(false);
  const [isExitPollDialogOpen, setIsExitPollDialogOpen] = useState(false);
  const [releaseType, setReleaseType] = useState('direct');
  const [voterPreference, setVoterPreference] = useState('straight');

  // Snackbar state for showing messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleStubReleaseSubmit = async () => {
    if (!user || !voter) return;

    try {
      // Check if stub release already exists for this voter
      const stubReleaseQuery = query(
        collection(db, 'stubReleases'), 
        where('voterId', '==', voter.id)
      );
      const existingStubReleases = await getDocs(stubReleaseQuery);

      if (!existingStubReleases.empty) {
        setSnackbarMessage('Stub release already exists for this voter');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsStubReleaseDialogOpen(false);
        return;
      }

      await addDoc(collection(db, 'stubReleases'), {
        voterId: voter.id,
        voterName: voter.name,
        precinct: voter.precinct,
        barangay: voter.barangay,
        city_municipality: voter.city_municipality,
        province: voter.province,
        district: voter.district,
        releaseType,
        voterPreference,
        addedBy: user.display_name ?? user.email ?? user.uid,
        addedAt: Timestamp.now()
      });
      
      setSnackbarMessage('Stub release added successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsStubReleaseDialogOpen(false);
    } catch (error) {
      console.error('Error adding stub release:', error);
      setSnackbarMessage('Error adding stub release');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleFinalReleaseSubmit = async () => {
    if (!user || !voter) return;

    try {
      // Check if final release already exists for this voter
      const finalReleaseQuery = query(
        collection(db, 'finalReleases'), 
        where('voterId', '==', voter.id)
      );
      const existingFinalReleases = await getDocs(finalReleaseQuery);

      if (!existingFinalReleases.empty) {
        setSnackbarMessage('Final release already exists for this voter');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsFinalReleaseDialogOpen(false);
        return;
      }

      await addDoc(collection(db, 'finalReleases'), {
        voterId: voter.id,
        voterName: voter.name,
        precinct: voter.precinct,
        barangay: voter.barangay,
        city_municipality: voter.city_municipality,
        province: voter.province,
        district: voter.district,
        addedBy: user.display_name ?? user.email ?? user.uid,
        addedAt: Timestamp.now()
      });
      
      setSnackbarMessage('Final release added successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsFinalReleaseDialogOpen(false);
    } catch (error) {
      console.error('Error adding final release:', error);
      setSnackbarMessage('Error adding final release');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleExitPollSubmit = async () => {
    if (!user || !voter) return;

    try {
      // Check if exit poll already exists for this voter
      const exitPollQuery = query(
        collection(db, 'exitPolls'), 
        where('voterId', '==', voter.id)
      );
      const existingExitPolls = await getDocs(exitPollQuery);

      if (!existingExitPolls.empty) {
        setSnackbarMessage('Exit poll already exists for this voter');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsExitPollDialogOpen(false);
        return;
      }

      await addDoc(collection(db, 'exitPolls'), {
        voterId: voter.id,
        voterName: voter.name,
        precinct: voter.precinct,
        barangay: voter.barangay,
        city_municipality: voter.city_municipality,
        province: voter.province,
        district: voter.district,
        addedBy: user.display_name ?? user.email ?? user.uid,
        addedAt: Timestamp.now()
      });
      
      setSnackbarMessage('Exit poll added successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsExitPollDialogOpen(false);
    } catch (error) {
      console.error('Error adding exit poll:', error);
      setSnackbarMessage('Error adding exit poll');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleUpmerge = async () => {
    setUpmergeClicked(true);

    const latestPollResponse = pollResponses[0];

    // create a inversed copy of the pollResponses array
    const pollResponsesAsc: PollResponse[] = [...pollResponses];
    pollResponsesAsc.reverse();

    let responses: { [question: string]: ResponseValue; } | null = null;
    for (const pr of pollResponsesAsc) {
      if (!responses) {
        responses = pr.responses;
        continue;
      }

      for (const q in pr.responses) {
        responses[q] = pr.responses[q];
      }
    }

    if (!responses) {
      console.log('No responses found, skipping...');
      return;
    }

    // check if the responses object has the name number of fields in latestPollResponse.responses
    let allSame = Object.keys(responses).length === Object.keys(latestPollResponse.responses).length;
    if (allSame) {
      // check if responses is same as pollResponse.responses question by question
      for (const q in responses) {
        if (responses[q] !== latestPollResponse.responses[q]) {
          allSame = false;
          break;
        }
      }
    }

    if (!allSame) {
      // add a new pollResponse in the collection, set submittedBy to Upmerge
      const newPollResponseRef = doc(db, 'pollResponses', uuidv4());
      const upmergedResponse = {
        ...latestPollResponse,
        responses,
        id: newPollResponseRef.id,
        submittedBy: 'Upmerge',
        submittedAt: Timestamp.now()
      };
      await setDoc(newPollResponseRef, upmergedResponse);

      // update pollResponses state
      const updatedPollResponses = [...pollResponses];
      updatedPollResponses.unshift(upmergedResponse);
      setPollResponses(updatedPollResponses);
    }
  }

  const loadVoterPollResponses = async (user: User, voter: Voter) => {
    try {      
      // Fetch poll responses
      const voterPollResponsesPath =
        `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}/pollResponses`;
      const pollResponsesColRef = collection(db, voterPollResponsesPath);
      const q = query(pollResponsesColRef, orderBy('submittedAt', 'desc'));
      const pollResponsesSnap = await getDocs(q);
      const _pollResponses = pollResponsesSnap.docs.map((d) => ({
        id: d.id,
        ...d.data()
      } as PollResponse));
      setPollResponses(_pollResponses);
    } catch (error) {
      console.error('Error fetching poll responses:', error);
    }
  }

  const handleValidatePollResponse = async (user: User, response: PollResponse) => {
    if (!user || !user.permissions.validate_polls || !voter) {
      console.error('User does not have permission to validate polls or voter is null');
      return;
    }

    try {
      const voterPollResponsesPath =
        `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}/pollResponses/${response.id}`;
      
      const pollResponseRef = doc(db, voterPollResponsesPath);

      const validatedBy = user.display_name ?? user.email ?? user.uid;
      const validatedAt = Timestamp.now();
      
      await updateDoc(pollResponseRef, {
        validated: true,
        validatedBy: validatedBy,
        validatedAt: validatedAt
      });

      // Also update the poll response in the voter's document.
      // This document always represent the latest poll response.
      const voterLatestPollResponsesPath =
        `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;
      
      const latestPollResponseRef = doc(db, voterLatestPollResponsesPath);
      await updateDoc(latestPollResponseRef, {
        validated: true,
        validatedBy: validatedBy,
        validatedAt: validatedAt
      });
      
      // Update the ones added by the pollsters
      const pollsterEntryPath = `pollResponses/${response.id}`;
      const pollsterEntryRef = doc(db, pollsterEntryPath);
      await updateDoc(pollsterEntryRef, {
        validated: true,
        validatedBy: validatedBy,
        validatedAt: validatedAt
      });

      // Update local state to reflect validation
      const updatedPollResponses = pollResponses.map(pr => 
        pr.id === response.id 
          ? { 
              ...pr, 
              validated: true, 
              validatedBy: user.uid, 
              validatedAt: Timestamp.now() 
            } 
          : pr
      );
      setPollResponses(updatedPollResponses);
    } catch (error) {
      console.error('Error validating poll response:', error);
    }
  }

  const loadVoterGroups = async (voter: Voter) => {
    try {
      // Fetch voter groups where this voter is a member
      const voterGroupsRef = collection(db, 'voterGroups');
      const q = query(
        voterGroupsRef, 
        where('members', 'array-contains', { id: voter.id, name: voter.name })
      );
      const voterGroupsSnap = await getDocs(q);
      const _voterGroups = voterGroupsSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as VoterGroup);
      setVoterGroups(_voterGroups);
    } catch (error) {
      console.error('Error fetching voter groups:', error);
    }
  }

  useEffect(() => {
    if (user && voter) {
      Promise.all([
        loadVoterPollResponses(user, voter),
        loadVoterGroups(voter)
      ]).then(() => {
        setLoading(false);
      })
    }
  }, [user, voter]);

  useEffect(() => {  
    const fetchVoter = async (voterId: string) => {
      try {
        const voterDocRef = doc(db, 'voters', voterId);
        const voterDocSnap = await getDoc(voterDocRef);

        if (voterDocSnap.exists()) {
          const fetchedVoter = voterDocSnap.data() as Voter;
          setVoter(fetchedVoter);
        }
      } catch (error) {
        console.error('Error fetching voter:', error);
      }
    };

    if (!voter && voterId) {
      fetchVoter(voterId);
    }
  }, [voterId, voter]);

  if (loading) {
    return <Typography>Loading voter data...</Typography>;
  }

  if (!voter) {
    return <Typography>Voter not found</Typography>;
  }

  return (
    <Container maxWidth="md" sx={{ p: 2 }}>
      {showBackButton && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">Voter Details</Typography>
          <Box display="flex" gap={2}>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => setIsQRCodeDialogOpen(true)}
            >
              Show QR Code
            </Button>
          </Box>
        </Box>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Name:</Typography>
              <Typography>{voter.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Province:</Typography>
              <Typography>{voter.province}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">City/Municipality:</Typography>
              <Typography>{voter.city_municipality}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Barangay:</Typography>
              <Typography>{voter.barangay}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Precinct:</Typography>
              <Typography>{voter.precinct}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Organizations:</Typography>
              <Typography>
                {voter.organizations?.map(org => org).join(', ') || 'No organizations'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                {user?.permissions.scan_qr_stub_release && (
                  <Button 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    onClick={() => setIsStubReleaseDialogOpen(true)}
                  >
                    Stub Release
                  </Button>
                )}
                {user?.permissions.scan_qr_final_release && (
                  <Button 
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                    onClick={() => setIsFinalReleaseDialogOpen(true)}
                  >
                    Final Release
                  </Button>
                )}
                {user?.permissions.scan_qr_exit_poll && (
                  <Button 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    onClick={() => setIsExitPollDialogOpen(true)}
                  >
                    Exit Poll
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {voterGroups.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Voter Groups</Typography>
            {voterGroups.map((group) => (
              <Card key={group.id} sx={{ mb: 1 }}>
                <CardContent>
                  <Typography variant="body1">
                    <strong>{group.name}</strong> 
                    {` (${group.level} level)`}
                  </Typography>
                  <Typography variant="body2">
                    Leader: {group.leader.name}
                  </Typography>
                  {group.purok && (
                    <Typography variant="body2">
                      Purok: {group.purok}
                    </Typography>
                  )}
                  {group.barangay && (
                    <Typography variant="body2">
                      Barangay: {group.barangay}
                    </Typography>
                  )}
                  {group.city_municipality && (
                    <Typography variant="body2">
                      City/Municipality: {group.city_municipality}
                    </Typography>
                  )}
                  {group.province && (
                    <Typography variant="body2">
                      Province: {group.province}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {pollResponses.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" gutterBottom>Poll Responses</Typography>
              {user?.permissions.validate_polls && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleUpmerge}
                  disabled={upmergeClicked}
                >
                  Upmerge
                </Button>
              )}
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Poll Title</TableCell>
                    <TableCell>Submitted By</TableCell>
                    <TableCell>Submitted At</TableCell>
                    <TableCell>Responses</TableCell>
                    {user?.permissions.validate_polls && (
                      <TableCell>Validate</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pollResponses.map((response, index) => (
                    <TableRow key={index}>
                      <TableCell>{response.pollTitle}</TableCell>
                      <TableCell>{response.submittedBy}</TableCell>
                      <TableCell>
                        {response.submittedAt 
                          ? new Date(response.submittedAt.seconds * 1000).toLocaleString() 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {Object.entries(response.responses || {}).map(([question, answer]) => (
                          <div key={question}>
                            <strong>{question}:</strong> {answer}
                          </div>
                        ))}
                      </TableCell>
                      {user?.permissions.validate_polls && (
                        <TableCell>
                          {index === 0 && !response.validated ? (
                            <Button 
                              variant="contained" 
                              color="primary"
                              onClick={() => handleValidatePollResponse(user, response)}
                            >
                              Validate
                            </Button>
                          ) : (
                            response.validated ? 'Validated' : 'N/A'
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog 
        open={isQRCodeDialogOpen} 
        onClose={() => setIsQRCodeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{voter?.id}</DialogTitle>
        <DialogContent>
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="100%"
            mt={2}
          >
            {voter && (
              <QRCodeSVG 
                value={voter.id} 
                size={256} 
                level="H"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsQRCodeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stub Release Dialog */}
      <Dialog 
        open={isStubReleaseDialogOpen} 
        onClose={() => setIsStubReleaseDialogOpen(false)}
      >
        <DialogTitle>Stub Release Confirmation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Release Type</InputLabel>
              <Select
                value={releaseType}
                label="Release Type"
                onChange={(e) => setReleaseType(e.target.value)}
              >
                <MenuItem value="direct">Direct Release</MenuItem>
                <MenuItem value="leader">Leader Released</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Voter Preference</InputLabel>
              <Select
                value={voterPreference}
                label="Voter Preference"
                onChange={(e) => setVoterPreference(e.target.value)}
              >
                <MenuItem value="straight">Straight Voting</MenuItem>
                <MenuItem value="organization">Exclusive to an Organization</MenuItem>
                <MenuItem value="position">Exclusive to a Specific Position</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsStubReleaseDialogOpen(false)}>Cancel</Button>
          <Button 
            color="primary" 
            variant="contained" 
            onClick={handleStubReleaseSubmit}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Release Dialog */}
      <Dialog 
        open={isFinalReleaseDialogOpen} 
        onClose={() => setIsFinalReleaseDialogOpen(false)}
      >
        <DialogTitle>Final Release Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to mark this voter for final release?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFinalReleaseDialogOpen(false)}>Cancel</Button>
          <Button 
            color="secondary" 
            variant="contained" 
            onClick={handleFinalReleaseSubmit}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Poll Dialog */}
      <Dialog 
        open={isExitPollDialogOpen} 
        onClose={() => setIsExitPollDialogOpen(false)}
      >
        <DialogTitle>Exit Poll Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to add this voter to exit polls?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExitPollDialogOpen(false)}>Cancel</Button>
          <Button 
            color="success" 
            variant="contained" 
            onClick={handleExitPollSubmit}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for displaying messages */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VoterDetails;
